import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Button, Space, Tag, Typography, Table, Select, Tooltip, Alert } from 'antd';
import { DollarOutlined, LinkOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../../utils/config';
import { formatMoney } from '../../../utils/currency';

const { Text } = Typography;

// 26.10 (F4 YS): lokasyonun YS kira hakedişleri, muhasebe kira giderleri ve eski Fedora
// (PHP) kira ödeme kayıtları — salt-okur (sql_queries 'lokasyon-ys-odemeleri').
// 27.10: LocationDetailPage'den ortak bileşene alındı; asıl lokasyon detayında
// (CardDetailPage, /detay/:id) da 'YS Ödemeleri' butonuyla açılır.

// lokasyon-ys-odemeleri 'Kaynak' değerleri
export const YS_ODEME_KAYNAK_RENK = {
  'Kira Hakedişi': 'blue',
  'Kira Gideri (Muhasebe)': 'purple',
  'Ödeme Kaydı (Eski Fedora)': 'cyan',
};
// 'Durum İzlenmiyor': PHP odm_taksit.durum 2025'ten beri işlenmiyor; ödenmiş de olabilir (gri)
// 27.10 düzeltmesi: gft durumları YS ekranlarıyla aynı — 'Ödeme Talebi Açıldı' (Finans'ta açık
// nf_odeme_talebi var) ve 'Ödeme Talebi Silinmiş' (bağ duruyor ama talep Finans'ta silinmiş, gri).
const YS_ODEME_DURUM_RENK = {
  'Ödendi': 'green', 'Ödenmiş': 'green', 'Kesildi': 'orange', 'Kesilecek': 'gold',
  'Ödeme Talebi Açıldı': 'blue', 'Ödeme Talebi Silinmiş': 'default',
  'Ödenecek': 'gold', 'Ödenecek (Onaylı)': 'lime', 'Gider Kaydı': 'default',
  'Durum İzlenmiyor': 'default',
};
const DURUM_IZLENMIYOR = 'Durum İzlenmiyor';
const DURUM_TALEP_SILINMIS = 'Ödeme Talebi Silinmiş';
const TALEP_SILINMIS_IPUCU = "Ödeme talebi Finans'ta silinmiş; Sözleşme & Fatura → Yer Sahibi Fatura → Talepler'den yeniden açılabilir";

const tarihMetni = (v) => {
  if (!v || String(v).startsWith('0000')) return '-';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('tr-TR');
};
const metinSirala = (alan) => (a, b) => String(a[alan] ?? '').localeCompare(String(b[alan] ?? ''), 'tr', { numeric: true });

export default function YsOdemeleriPanel({ maptriksId, style, variant }) {
  const navigate = useNavigate();
  const [ysOdemeleri, setYsOdemeleri] = useState([]);
  const [ysOdemeLoading, setYsOdemeLoading] = useState(false);
  const [ysOdemeKaynak, setYsOdemeKaynak] = useState('all');

  useEffect(() => {
    let mounted = true;
    async function fetchYsOdemeleri() {
      if (!maptriksId || !/^\d+$/.test(String(maptriksId))) return;
      try {
        setYsOdemeLoading(true);
        const res = await axios.post(`${API_BASE_URL}/list/execute-query/lokasyon-ys-odemeleri`, {
          params: { maptriksId: Number(maptriksId) }
        });
        if (mounted) setYsOdemeleri(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (mounted) setYsOdemeleri([]);
      } finally {
        if (mounted) setYsOdemeLoading(false);
      }
    }

    fetchYsOdemeleri();
    return () => { mounted = false; };
  }, [maptriksId]);

  const ysOdemeGorunen = useMemo(() => (
    ysOdemeKaynak === 'all' ? ysOdemeleri : ysOdemeleri.filter(r => r['Kaynak'] === ysOdemeKaynak)
  ), [ysOdemeleri, ysOdemeKaynak]);

  const ysOdemeOzet = useMemo(() => {
    const ozet = {};
    ysOdemeleri.forEach(r => {
      const k = r['Kaynak'];
      if (!ozet[k]) ozet[k] = { adet: 0, tutar: 0 };
      ozet[k].adet += 1;
      ozet[k].tutar += parseFloat(r['Tutar']) || 0;
    });
    return ozet;
  }, [ysOdemeleri]);

  const izlenmeyenSayi = useMemo(
    () => ysOdemeleri.filter(r => r['Durum'] === DURUM_IZLENMIYOR).length,
    [ysOdemeleri]
  );

  return (
    <Card
      size="small"
      variant={variant}
      style={style}
      title={
        <Space>
          <DollarOutlined />
          <Text strong>YS Kira Faturaları ve Ödemeleri</Text>
        </Space>
      }
      extra={
        <Space wrap>
          <Select
            size="small"
            style={{ width: 210 }}
            value={ysOdemeKaynak}
            onChange={setYsOdemeKaynak}
            options={[
              { value: 'all', label: 'Tüm kaynaklar' },
              ...Object.keys(YS_ODEME_KAYNAK_RENK).map(k => ({
                value: k,
                label: `${k} (${ysOdemeOzet[k]?.adet || 0})`
              }))
            ]}
          />
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={() => navigate(`/sozlesme-fatura?tab=ys-fatura&lokasyon=${maptriksId}`)}
          >
            Sözleşme Fatura İşlemleri
          </Button>
        </Space>
      }
    >
      {Object.keys(ysOdemeOzet).length > 0 && (
        <Space wrap size={[16, 4]} style={{ marginBottom: 8 }}>
          {Object.entries(ysOdemeOzet).map(([k, o]) => (
            <Text key={k} type="secondary" style={{ fontSize: 12 }}>
              <Tag color={YS_ODEME_KAYNAK_RENK[k] || 'default'} style={{ marginRight: 4 }}>{k}</Tag>
              {o.adet} kayıt · {formatMoney(o.tutar, 'TRY')}
            </Text>
          ))}
        </Space>
      )}
      {izlenmeyenSayi > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8, padding: '4px 12px' }}
          message={`Eski Fedora (PHP) ödeme kayıtlarında ödeme durumu 2025'ten beri işlenmiyor: ${izlenmeyenSayi} kayıt '${DURUM_IZLENMIYOR}' gösteriliyor; bu ödemeler yapılmış olabilir, ödenmemiş sayılmamalı.`}
        />
      )}
      <Table
        rowKey="id"
        size="small"
        loading={ysOdemeLoading}
        dataSource={ysOdemeGorunen}
        scroll={{ x: 'max-content' }}
        pagination={ysOdemeGorunen.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: false } : false}
        locale={{ emptyText: 'Bu lokasyon için YS kira hakedişi / ödeme kaydı bulunamadı' }}
        columns={[
          {
            title: 'Kaynak',
            dataIndex: 'Kaynak',
            key: 'kaynak',
            sorter: metinSirala('Kaynak'),
            render: (v) => <Tag color={YS_ODEME_KAYNAK_RENK[v] || 'default'}>{v}</Tag>,
          },
          {
            title: 'Tarih',
            dataIndex: 'Tarih',
            key: 'tarih',
            defaultSortOrder: 'descend',
            sorter: metinSirala('Tarih'),
            render: tarihMetni,
          },
          {
            title: 'Dönem',
            key: 'donem',
            sorter: metinSirala('Dön. Baş.'),
            render: (_, r) => (r['Dön. Baş.'] || r['Dön. Bit.'])
              ? `${tarihMetni(r['Dön. Baş.'])} – ${tarihMetni(r['Dön. Bit.'])}` : '-',
          },
          { title: 'Cari', dataIndex: 'Cari', key: 'cari', sorter: metinSirala('Cari'), render: (v) => v || '-' },
          { title: 'Kabin', dataIndex: 'Kabin', key: 'kabin', render: (v) => v || '-' },
          {
            title: 'Tutar',
            dataIndex: 'Tutar',
            key: 'tutar',
            align: 'right',
            sorter: (a, b) => (parseFloat(a['Tutar']) || 0) - (parseFloat(b['Tutar']) || 0),
            render: (v) => formatMoney(v, 'TRY'),
          },
          {
            title: 'Durum',
            dataIndex: 'Durum',
            key: 'durum',
            sorter: metinSirala('Durum'),
            render: (v) => {
              const tag = <Tag color={YS_ODEME_DURUM_RENK[v] || 'default'}>{v || '-'}</Tag>;
              if (v === DURUM_IZLENMIYOR) {
                return <Tooltip title="PHP ödeme ekranı bu kaydın ödendiğini işaretlemiyor; ödeme durumu bilinmiyor">{tag}</Tooltip>;
              }
              return v === DURUM_TALEP_SILINMIS ? <Tooltip title={TALEP_SILINMIS_IPUCU}>{tag}</Tooltip> : tag;
            },
          },
          {
            title: 'Ödeme Talebi',
            key: 'odemeTalebi',
            render: (_, r) => {
              if (!r['Ödeme Talebi']) {
                return r['Kaynak'] === 'Ödeme Kaydı (Eski Fedora)' && r['Vade'] ? `vade ${tarihMetni(r['Vade'])}` : '-';
              }
              // 27.10 düzeltmesi: '#N' yalnız talep Finans'ta açıksa ('Talep Açık' = 1) ya da kayıt
              // ödendiyse; bağ duruyor ama nf_odeme_talebi satırı yoksa gri 'silinmiş' yazılır.
              // (Kolon gelmezse — eski yanıt — Number(undefined) NaN olur, eski görünüm korunur.)
              const silinmis = Number(r['Talep Açık']) === 0 && r['Durum'] !== 'Ödendi';
              if (silinmis) {
                return (
                  <Tooltip title={TALEP_SILINMIS_IPUCU}>
                    <Text type="secondary">{`#${r['Ödeme Talebi']} (Finans'ta silinmiş)`}</Text>
                  </Tooltip>
                );
              }
              return (
                <span>
                  #{r['Ödeme Talebi']}
                  {r['Vade'] ? ` · vade ${tarihMetni(r['Vade'])}` : ''}
                  {r['Talep Durumu'] ? <Tag style={{ marginLeft: 4 }}>{r['Talep Durumu']}</Tag> : null}
                </span>
              );
            },
          },
          { title: 'Evrak No', dataIndex: 'Evrak No', key: 'evrak', render: (v) => v || '-' },
          {
            title: 'Açıklama',
            dataIndex: 'Açıklama',
            key: 'aciklama',
            width: 260,
            ellipsis: { showTitle: false },
            render: (v) => (v ? <Tooltip title={v}><span>{v}</span></Tooltip> : '-'),
          },
        ]}
      />
    </Card>
  );
}
